import { useAuth } from '../contexts/AuthContext';
import { createApiHeadersGetter } from '../utils/api-headers';
import { PACKAGES_URL } from '../config/api-config';

interface APIError {
  message: string;
  code?: string;
  details?: any;
  status?: number;
}

export class ApiError extends Error {
  status: number;
  details?: any;

  constructor(message: string, status: number, details?: any) {
    super(message);
    this.status = status;
    this.details = details;
    this.name = 'ApiError';
  }
}

export interface ExecutionRecord {
  packageId: string;
  packageName: string;
  executionId: string;
  executionDate: string;
  result?: any;
  error?: string;
  duration?: number;
  userId?: string;
  ownerId?: string;
  aiLog?: string;
  executionLog?: string;
}

export interface ExecutionRecordsResponse {
  items: ExecutionRecord[];
  metadata: {
    count: number;
    scannedCount: number;
    lastEvaluatedKey: string | null;
    pageNumber: number;
  };
}

export const useExecutionRecordsApi = () => {
  const { getAuthToken } = useAuth();
  const getHeaders = createApiHeadersGetter(getAuthToken);

  const getExecutionRecords = async (
    packageId: string,
    pageSize: number = 10,
    pageNumber: number = 1,
    filters?: {
      packageName?: string;
      executionId?: string;
      ownerId?: string;
      status?: string;
      dateFrom?: string | null;
      dateTo?: string | null;
    }
  ): Promise<ExecutionRecordsResponse> => {
    try {
      const headers = await getHeaders();
      const response = await fetch(
        `${PACKAGES_URL}/execution-records`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            packageId,
            pageSize,
            pageNumber,
            filters
          })
        }
      );

      if (!response.ok) {
        let errorMessage = 'Failed to fetch execution records';
        let errorDetails;
        
        try {
          // Try to parse error response as JSON
          const errorData: APIError = await response.json();
          errorMessage = errorData.message || errorMessage;
          errorDetails = errorData.details;
        } catch (parseError) {
          // If JSON parsing fails, use status text
          errorMessage = response.statusText || errorMessage;
        }
        
        throw new ApiError(errorMessage, response.status, errorDetails);
      }

      return await response.json();
    } catch (error: any) {
      console.error('Error fetching execution records:', error);
      // If it's already an ApiError, rethrow it
      if (error instanceof ApiError) {
        throw error;
      }
      // Otherwise wrap it in an ApiError
      throw new ApiError(error.message || 'Unknown error occurred', 500);
    }
  };

  return {
    getExecutionRecords
  };
}; 